import { ChangeEvent, useEffect, useRef, useState } from "react";
import { faChevronUp, faChevronDown, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppConstants } from "../../configs";
import { useFunctionalityCalls } from "../../pages/hooks";
import { UtilsJQuery } from "../../utils";
type Props = {
  custom1Name: string | "";
  custom1Value: string | "";
  custom2Name: string | "";
  custom2Value: string | "";
  custom3Name: string | "";
  custom3Value: string | "";
  custom4Name: string | "";
  custom4Value: string | "";
  departmentName?: string;
  departmentValue?: string;
  locationName?: string;
  locationValue?: string;
  callId?: string;
  userRole?: string;
  onRefreshSummary?: () => void | Promise<void>;
};
export function CallSummaryCustomField(props: Props): JSX.Element {
  const {
    custom1Name,
    custom1Value,
    custom2Name,
    custom2Value,
    custom3Name,
    custom3Value,
    custom4Name,
    custom4Value,
    departmentName,
    departmentValue,
    locationName,
    locationValue,
    callId,
    userRole,
    onRefreshSummary,
  } = props;
  const { onUpdateIntakesAfterFinish } = useFunctionalityCalls();
  const isAdmin = userRole === AppConstants.UserRoles.Admin;
  const hasAnyFieldName = Boolean(
    custom1Name ||
    custom2Name ||
    custom3Name ||
    custom4Name ||
    departmentName ||
    locationName
  );
  const displayValue = (value?: string | null) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    return value;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  const collapseId = "custom-input-collapse";
  const submitButtonClass = "call-summary-custom-fields-submit";
  const [formValues, setFormValues] = useState({
    custom1Value: custom1Value ?? "",
    custom2Value: custom2Value ?? "",
    custom3Value: custom3Value ?? "",
    custom4Value: custom4Value ?? "",
    departmentValue: departmentValue ?? "",
    locationValue: locationValue ?? "",
  });

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: departmentValue ?? "",
      locationValue: locationValue ?? "",
    });
  }, [custom1Value, custom2Value, custom3Value, custom4Value, departmentValue, locationValue]);

  const openAccordion = () => {
    const collapseEl = document.getElementById(collapseId);
    if (!collapseEl) return;
    const anyWindow = window as any;
    if (anyWindow?.$ && typeof anyWindow.$ === "function") {
      try {
        anyWindow.$(collapseEl).collapse("show");
        return;
      } catch {
        // fallback below
      }
    }
    if (!collapseEl.classList.contains("show")) {
      collapseEl.classList.add("show");
      toggleButtonRef.current?.classList.remove("collapsed");
      toggleButtonRef.current?.setAttribute("aria-expanded", "true");
    }
  };

  const startEdit = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: departmentValue ?? "",
      locationValue: locationValue ?? "",
    });
    setIsEditing(true);
    setEditPanelOpen(false);
    window.requestAnimationFrame(() => setEditPanelOpen(true));
    openAccordion();
  };

  const onValueChange =
    (
      key:
        | "custom1Value"
        | "custom2Value"
        | "custom3Value"
        | "custom4Value"
        | "departmentValue"
        | "locationValue"
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
      };

  const closeEditPanel = () => {
    setEditPanelOpen(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsEditing(false);
    }, 200);
  };

  const onSubmit = async () => {
    if (!callId) {
      console.warn("Missing callId for intake update.");
      return;
    }
    const btnLoading = UtilsJQuery.Ladda(`.${submitButtonClass}`);
    btnLoading.start?.();
    setIsSaving(true);
    try {
      await onUpdateIntakesAfterFinish(callId, {
        customInputRequestModels: [
          { criteriaType: 4, value: formValues.custom1Value },
          { criteriaType: 5, value: formValues.custom2Value },
          { criteriaType: 6, value: formValues.custom3Value },
          { criteriaType: 7, value: formValues.custom4Value },
          { criteriaType: 8, value: formValues.departmentValue },
          { criteriaType: 9, value: formValues.locationValue },
        ].filter(
          (entry) =>
            entry.value !== null &&
            entry.value !== undefined &&
            entry.value !== ""
        ),
      });
      await onRefreshSummary?.();
      closeEditPanel();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
      btnLoading.stop?.();
    }
  };

  const onCancel = () => {
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: departmentValue ?? "",
      locationValue: locationValue ?? "",
    });
    closeEditPanel();
  };
  return (
    <>
      <div className="card">
        <div
          className="card-header px-0 py-1 d-flex align-items-center position-relative"
          id="headingCustomInput"
        >
          <button
            ref={toggleButtonRef}
            className="btn btn-link text-left text-decoration-none text-site siteAccordion collapsed flex-grow-1"
            type="button"
            data-toggle="collapse"
            data-target={`#${collapseId}`}
            aria-expanded="true"
            aria-controls={collapseId}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="font-weight-bold">Custom Fields Input </span>
              </div>
              <div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="closed-panel-icon"
                />
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className="open-panel-icon"
                />
              </div>
            </div>
          </button>
          <div className="position-absolute" style={{right: "34px"}}>
          {isAdmin ? (
            <button
              type="button"
              className={`btn btn-link text-site text-decoration-none px-2 ${hasAnyFieldName ? "" : "text-muted"}`}
              disabled={!hasAnyFieldName}
              aria-disabled={!hasAnyFieldName}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                startEdit();
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" />
              Edit
            </button>
          ) : null}
          </div>
        </div>
        <div
          className="collapse"
          id={collapseId}
          aria-labelledby="headingCustomInput"
          data-parent="#call-summary-panels"
        >
          <div className="card-body px-3 py-2">
            {isAdmin && isEditing ? (
              <div
                className="border rounded p-2 mb-3 bg-light"
                style={{
                  transformOrigin: "top",
                  transform: editPanelOpen ? "scaleY(1)" : "scaleY(0.98)",
                  opacity: editPanelOpen ? 1 : 0,
                  marginTop: editPanelOpen ? "0" : "-6px",
                  overflow: "hidden",
                  transition: "transform 240ms ease, opacity 200ms ease, margin-top 200ms ease",
                  willChange: "transform, opacity",
                }}
              >
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    {custom1Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom1Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom1Value}
                          onChange={onValueChange("custom1Value")}
                        />
                      </div>
                    ) : null}
                    {custom2Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom2Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom2Value}
                          onChange={onValueChange("custom2Value")}
                        />
                      </div>
                    ) : null}
                    {custom3Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom3Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom3Value}
                          onChange={onValueChange("custom3Value")}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 border-sm-top">
                    {custom4Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom4Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom4Value}
                          onChange={onValueChange("custom4Value")}
                        />
                      </div>
                    ) : null}
                    {departmentName ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{departmentName}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.departmentValue}
                          onChange={onValueChange("departmentValue")}
                        />
                      </div>
                    ) : null}
                    {locationName ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{locationName}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.locationValue}
                          onChange={onValueChange("locationValue")}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mr-2"
                    onClick={onCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary btn-sm ${submitButtonClass}`}
                    onClick={onSubmit}
                    disabled={isSaving}
                  >
                    Submit
                  </button>
                </div>
              </div>
            ) : null}
            <div className="row mt-1">
              {!hasAnyFieldName ? (
                <div className="col-12 text-muted">There are no entries</div>
              ) : null}
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                {custom1Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom1Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom1Value)}
                    </div>
                  </div>
                ) : null}
                {custom2Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom2Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom2Value)}
                    </div>
                  </div>
                ) : null}
                {custom3Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom3Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom3Value)}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 border-sm-top">
                {custom4Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom4Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom4Value)}
                    </div>
                  </div>
                ) : null}
                {departmentName ? (
                  <div className="row mb-2">
                    <div className="col-6">{departmentName}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(departmentValue)}
                    </div>
                  </div>
                ) : null}
                {locationName ? (
                  <div className="row mb-2">
                    <div className="col-6">{locationName}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(locationValue)}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
